import React, { useState } from "react";
import { ArrowRightIcon, PlusIcon } from "@icons";
import { Button } from "@design-system/Button/Button";
import type { ButtonSize } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import {
    defaultSizeByVariant,
    sizeOptionsByVariant,
} from "./ButtonPlayground.lib";
import type { ButtonVariant, GhostLabelStyle } from "./ButtonPlayground.lib";
import {
    ButtonPlaygroundControls,
    type ButtonFeatureFlags,
} from "./ButtonPlaygroundControls";
import { ButtonMatrix } from "./ButtonPlaygroundMatrix";
import { ButtonWrappers } from "./ButtonWrappersPlayground";
import {
    useAnatomyMode,
    type AnatomyForcedState,
} from "./componentAnatomyMode";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";

interface SandboxCommonProps {
    type: "button";
    disabled: boolean;
    block: boolean;
    /** Playground-only hook the stylesheet mirrors every state rule onto. */
    "data-forced-state"?: AnatomyForcedState;
}

/** Mirrors the component's icon union: icon-only buttons must carry a label. */
type SandboxSlotProps =
    | { iconOnly: true; "aria-label": string }
    | {
          iconOnly?: false;
          leftIcon?: React.ReactNode;
          rightIcon?: React.ReactNode;
      };

/**
 * Stand-in for the squarish slot content the real chips will be (tag count,
 * entity icon, shortcut key). `data-slot-box` is the marker the stylesheet
 * looks for to tighten that side's padding by one 4px step.
 */
const SlotBox = () => (
    <span
        data-slot-box=""
        style={{
            display: "inline-flex",
            width: "16px",
            height: "16px",
            borderRadius: "4px",
            backgroundColor: "var(--bg-accent-subtle)",
        }}
    />
);

export const ButtonPlayground = () => {
    const [variant, setVariant] = useState<ButtonVariant>("primary");
    const [size, setSize] = useState<ButtonSize>("md");
    const [labelStyle, setLabelStyle] = useState<GhostLabelStyle>("s-regular");
    const [features, setFeatures] = useState<ButtonFeatureFlags>({
        isDisabled: false,
        hasLeftIcon: false,
        hasRightIcon: false,
        hasSlotBox: false,
        isIconOnly: false,
        isBlock: false,
        hasTooltip: false,
    });
    const anatomy = useAnatomyMode({
        component: "button",
        variantId: variant,
        sizeId: size,
        isDisabled: features.isDisabled,
    });

    const {
        isDisabled,
        hasLeftIcon,
        hasRightIcon,
        hasSlotBox,
        isIconOnly,
        isBlock,
        hasTooltip,
    } = features;

    const sizeOptions: readonly ButtonSize[] = sizeOptionsByVariant[variant];

    const handleVariantChange = (nextVariant: ButtonVariant) => {
        setVariant(nextVariant);

        const nextOptions: readonly ButtonSize[] =
            sizeOptionsByVariant[nextVariant];

        if (!nextOptions.some((option) => option === size)) {
            setSize(defaultSizeByVariant[nextVariant]);
        }
    };

    const commonProps: SandboxCommonProps = {
        type: "button",
        disabled: isDisabled,
        block: isBlock,
        "data-forced-state": anatomy.forcedState,
    };

    const leftSlotNode = hasSlotBox ? <SlotBox /> : <PlusIcon />;
    const rightSlotNode = hasSlotBox ? <SlotBox /> : <ArrowRightIcon />;

    const slotProps: SandboxSlotProps = isIconOnly
        ? { iconOnly: true, "aria-label": "Add item" }
        : {
              leftIcon: hasLeftIcon ? leftSlotNode : undefined,
              rightIcon: hasRightIcon ? rightSlotNode : undefined,
          };

    const label = isIconOnly ? <PlusIcon /> : "Button";

    // The switch is what keeps the sandbox honest: each branch narrows `size`
    // to what its variant actually accepts, so no cast is needed anywhere.
    const renderSandboxButton = (): React.ReactElement => {
        switch (variant) {
            case "primary":
            case "secondary":
                return (
                    <Button
                        variant={variant}
                        size={size}
                        {...commonProps}
                        {...slotProps}
                    >
                        {label}
                    </Button>
                );
            case "tertiary":
            case "destructive":
                return (
                    <Button
                        variant={variant}
                        size={size === "xs" ? "sm" : size}
                        {...commonProps}
                        {...slotProps}
                    >
                        {label}
                    </Button>
                );
            case "ghost":
                return (
                    <Button
                        variant="ghost"
                        size="md"
                        labelStyle={labelStyle}
                        {...commonProps}
                        {...slotProps}
                    >
                        {label}
                    </Button>
                );
            case "accent":
                return (
                    <Button
                        variant="accent"
                        size="md"
                        {...commonProps}
                        {...slotProps}
                    >
                        {label}
                    </Button>
                );
            case "link":
                return (
                    <Button
                        variant="link"
                        size={size === "xs" ? "xs" : "sm"}
                        {...commonProps}
                        {...slotProps}
                    >
                        {label}
                    </Button>
                );
        }
    };

    const sandboxButton = renderSandboxButton();
    const summary = `${variant} · ${size}`;

    return (
        <SandboxLayout
            controls={
                <>
                    {anatomy.control}
                    <ButtonPlaygroundControls
                        variant={variant}
                        onVariantChange={handleVariantChange}
                        size={size}
                        sizeOptions={sizeOptions}
                        onSizeChange={setSize}
                        labelStyle={labelStyle}
                        onLabelStyleChange={setLabelStyle}
                        features={features}
                        onFeaturesChange={setFeatures}
                    />
                </>
            }
        >
            <ShowcasePreview
                width={anatomy.stageWidth}
                summary={anatomy.isActive ? `x-ray · ${summary}` : summary}
                belowStage={
                    <>
                        <ButtonMatrix />
                        <ButtonWrappers />
                    </>
                }
                output={JSON.stringify(
                    {
                        variant,
                        size,
                        labelStyle: variant === "ghost" ? labelStyle : null,
                        disabled: isDisabled,
                        leftIcon: !isIconOnly && hasLeftIcon,
                        rightIcon: !isIconOnly && hasRightIcon,
                        iconOnly: isIconOnly,
                        slotChips: !isIconOnly && hasSlotBox,
                        block: isBlock,
                        tooltip: hasTooltip ? "Button tooltip" : null,
                        forcedState: anatomy.forcedState ?? null,
                    },
                    null,
                    2,
                )}
            >
                {anatomy.view ??
                    (hasTooltip ? (
                        <Tooltip content="Button tooltip">
                            {sandboxButton}
                        </Tooltip>
                    ) : (
                        sandboxButton
                    ))}
            </ShowcasePreview>
        </SandboxLayout>
    );
};
